import {
  useEffectWithController,
  useSubsequentEffect
} from '@carvana/vdp-react-hooks';
import { useContext, useRef, useState } from 'react';
import { MAX_PAGE_SIZE_DEFAULT } from '~/constants';
import FacetDataContext from '~/contexts/FacetDataContext';
import FinanceAndTradeContext, {
  formatFinanceInfo
} from '~/contexts/FinanceAndTradeContext';
import InventoryContext from '~/contexts/InventoryContext';
import { useUserData } from '~/contexts/UserContext';
import type { ConditionalTile } from '~/contracts';
import useGetLowInventory from '~/hooks/services/useGetLowInventory';
import useConditionalTiles from '~/hooks/useConditionalTiles';
import useGenerateInventoryRequest from '~/hooks/useGenerateInventoryRequest';
import { InventoryRequest, InventoryResponse } from '~/services/contracts';
import fetchInventory from '~/services/fetchInventory';
import generateErrorLogger from '~/utilities/errorHandlers';

const getPageSize = (conditionalTiles: ConditionalTile[]) =>
  MAX_PAGE_SIZE_DEFAULT - conditionalTiles.filter(v => v.active).length;

export default function useGetInventory(
  fetchedInventoryOnServer?: boolean
): void {
  const { setFacetData } = useContext(FacetDataContext);
  const { setFinanceInfo, setTradeInfo } = useContext(FinanceAndTradeContext);
  const {
    fetchInventoryTrigger,
    inventory: { setData, setError, setLoading, isLoaded },
    setSimilarVehicles
  } = useContext(InventoryContext);
  const conditionalTiles = useConditionalTiles();
  const request = useGenerateInventoryRequest(getPageSize(conditionalTiles));
  const requestForLowInventory = useRef<InventoryRequest>();
  const [inventoryResponse, setInventoryResponse] =
    useState<InventoryResponse>();
  const [shouldFetch, setShouldFetch] = useState(!fetchedInventoryOnServer);
  const getLowInventory = useGetLowInventory();
  const { isLoggedIn } = useUserData();

  useSubsequentEffect(() => {
    setShouldFetch(true);
    requestForLowInventory.current = request;
  }, [fetchInventoryTrigger, isLoggedIn, request]);

  useSubsequentEffect(() => {
    if (isLoaded) {
      setShouldFetch(false);
    }
  }, [isLoaded]);

  useEffectWithController(
    controller => {
      if (!shouldFetch) return;

      setLoading();

      setSimilarVehicles({
        searchRequestId: '',
        vehicles: []
      });

      fetchInventory(request, controller)
        .then(res => {
          const {
            facetData,
            financeInfo,
            inventory,
            searchRequestId,
            tradeInfo,
            userDeliveryInfo
          } = res.data;
          const formattedFinanceInfo =
            financeInfo && !financeInfo.pricingHasErrored
              ? formatFinanceInfo(financeInfo)
              : undefined;

          setData({
            inventory,
            searchRequestId,
            userDeliveryInfo
          });
          setFacetData(facetData);
          setFinanceInfo(formattedFinanceInfo);
          setInventoryResponse(res.data);
          setTradeInfo(tradeInfo);
        })
        .catch(
          generateErrorLogger(
            'Inventory',
            {},
            { callback: setError, controller }
          )
        );
    },
    [
      isLoggedIn,
      request,
      setData,
      setError,
      setFacetData,
      setFinanceInfo,
      setLoading,
      setSimilarVehicles,
      setTradeInfo,
      shouldFetch
    ]
  );

  useEffectWithController(
    controller => {
      if (inventoryResponse && requestForLowInventory.current) {
        void getLowInventory(
          requestForLowInventory.current,
          inventoryResponse,
          controller
        );
      }
    },
    [getLowInventory, inventoryResponse]
  );
}
